import { assoc, curry, keys, reduce } from 'ramda'

/**
 * Creates a new object with the own properties of the provided object, but the
 * keys renamed according to the keysMap object as `{oldKey: newKey}`.
 * When some key is not found in the keysMap, then it's passed as-is.
 *
 *
 * @sig {a: b} -> {a: *} -> {b: *}
 */
export const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj))
)

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe('renameKeys', () => {
    it('renames mapped keys and preserves others', () => {
      const result = renameKeys(
        { a: 'alpha', c: 'gamma' },
        { a: 1, b: 2, c: 3 }
      )

      expect(result).toEqual({ alpha: 1, b: 2, gamma: 3 })
    })

    it('returns a new object without mutating input', () => {
      const source = { a: 1 }
      const result = renameKeys({ a: 'b' }, source)

      expect(result).toEqual({ b: 1 })
      expect(source).toEqual({ a: 1 })
    })

    it('supports curried usage', () => {
      const renamer = renameKeys({ a: 'z' })

      expect(renamer({ a: 2, b: 3 })).toEqual({ b: 3, z: 2 })
    })
  })
}
